import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";
import { AuthLayout } from "./AuthLayout";
import { PasswordStrength } from "./PasswordStrength";
import { passwordRule } from "./passwordRules";
import { useSignup } from "@/api/hooks/auth";
import { errorMessage, isApiError } from "@/api/errors";
import { Field, FormError, FormSuccess } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const schema = z.object({
  name: z.string().trim().min(1, "auth.nameRequired"),
  email: z.string().trim().min(1, "auth.emailRequired").email("auth.emailInvalid"),
  password: passwordRule,
  organisation_name: z.string().trim().min(1, "auth.organisationRequired"),
});
type Values = z.infer<typeof schema>;

export function SignupPage() {
  const { t } = useTranslation();
  const signup = useSignup();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", email: "", password: "", organisation_name: "" },
  });
  const { errors } = form.formState;
  const onSubmit = form.handleSubmit((v) => signup.mutate(v));
  const e = signup.error;
  const serverError = signup.isError
    ? isApiError(e) && e.status === 404
      ? t("auth.signupUnavailable")
      : errorMessage(e, t("errors.generic"))
    : null;
  const err = (k?: string) => (k ? t(k) : null);

  return (
    <AuthLayout title={t("auth.signupTitle")} intro={t("auth.signupIntro")}>
      {signup.isSuccess ? (
        <FormSuccess message={t("auth.signupSuccess")} />
      ) : (
        <form noValidate onSubmit={onSubmit} className="flex flex-col gap-5">
          <FormError message={serverError} />
          <Field label={t("auth.yourName")} error={err(errors.name?.message)}>
            {(p) => <Input {...p} autoComplete="name" {...form.register("name")} />}
          </Field>
          <Field label={t("auth.email")} error={err(errors.email?.message)}>
            {(p) => <Input {...p} type="email" autoComplete="email" {...form.register("email")} />}
          </Field>
          <Field label={t("auth.password")} hint={t("auth.passwordHint")} error={err(errors.password?.message)}>
            {(p) => <Input {...p} type="password" autoComplete="new-password" {...form.register("password")} />}
          </Field>
          <PasswordStrength value={form.watch("password")} />
          <Field label={t("auth.organisationName")} error={err(errors.organisation_name?.message)}>
            {(p) => <Input {...p} autoComplete="organization" {...form.register("organisation_name")} />}
          </Field>
          <Button type="submit" className="w-full" disabled={signup.isPending}>
            {t("auth.createAccount")}
          </Button>
        </form>
      )}
      <p className="mt-6 text-sm">
        <Link to="/login" className="underline">
          {t("auth.backToSignIn")}
        </Link>
      </p>
    </AuthLayout>
  );
}
